/**
 * The whole record for one uid.
 *
 * The per-location table iterates Object.entries(record.firestore). The keys come from
 * obsessless-cloud-functions/src/users/user-data-map.json and change whenever a
 * collection is added there, so nothing here may hardcode them -- a hardcoded list is
 * the defect this whole feature exists to fix.
 */
import { Modal } from '../../../components/Modal';
import { UserLinks } from '../../../components/UserLinks';
import { useFormatUtils } from '../../../hooks/useFormatUtils';
import type { AccountDeletionRecord } from '../types/accountDeletionTypes';
import { isInconsistent, isStalled, needsAttention, sweepSummary } from '../utils/deletionFilters';
import {
  AttentionBadge,
  DeletionStatusPill,
  InconsistentBadge,
  PostHogOutcomeBadge,
  RevenueCatOutcomeBadge,
  SourceBadge,
  StalledBadge,
} from './DeletionBadges';

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div>
    <dt className="text-xs font-medium text-gray-500 uppercase tracking-wider">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900">{children}</dd>
  </div>
);

const SectionHeading = ({ children }: { children: React.ReactNode }) => (
  <h4 className="text-sm font-semibold text-gray-800 mb-2">{children}</h4>
);

/** Why a step has no data, said plainly rather than shown as a zero. */
const NotReached = ({ message }: { message: string }) => (
  <div className="bg-amber-50 border border-amber-200 rounded-md p-3 text-sm text-amber-800">
    {message}
  </div>
);

interface DeletionDetailModalProps {
  record: AccountDeletionRecord | null;
  onClose: () => void;
}

export const DeletionDetailModal = ({ record, onClose }: DeletionDetailModalProps) => {
  const { formatDate } = useFormatUtils();

  if (!record) return null;

  const locations = Object.entries(record.firestore);
  const summary = sweepSummary(record);

  return (
    <Modal isOpen={true} onClose={onClose} title="Account deletion record" size="xl">
      <div className="space-y-6">
        <div className="flex flex-wrap items-center gap-2">
          <DeletionStatusPill status={record.status} />
          <SourceBadge source={record.source} />
          {needsAttention(record) && <AttentionBadge />}
          {isStalled(record) && <StalledBadge />}
          {isInconsistent(record) && <InconsistentBadge />}
        </div>

        <div>
          <SectionHeading>User</SectionHeading>
          <div className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-sm text-gray-700 break-all">{record.uid}</span>
            <UserLinks userId={record.uid} />
          </div>
          <p className="mt-2 text-xs text-gray-500">
            The document id is the uid, which is also the RevenueCat app_user_id. The RevenueCat
            API cannot rename it, so the anonymized shell keeps it.
          </p>
        </div>

        <div>
          <SectionHeading>Timeline</SectionHeading>
          <dl className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <Field label="Requested">{formatDate(record.requestedAt)}</Field>
            <Field label="Last updated">{formatDate(record.updatedAt)}</Field>
            <Field label="Completed">{formatDate(record.completedAt)}</Field>
            <Field label="Attempts">
              <span className={needsAttention(record) ? 'font-semibold text-red-600' : ''}>
                {record.attemptCount}
              </span>
            </Field>
            <Field label="Registry version">{record.registryVersion}</Field>
          </dl>
        </div>

        <div>
          <SectionHeading>Step 1 — RevenueCat</SectionHeading>
          {record.revenueCat === null ? (
            <NotReached message="Step 1 never reported. RevenueCat runs before the Firestore sweep, so the local data should still be intact and the job is safe to retry." />
          ) : (
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Outcome">
                <RevenueCatOutcomeBadge audit={record.revenueCat} />
              </Field>
              <Field label="Attributes cleared">{record.revenueCat.attributesCleared}</Field>
              <Field label="Vendor attempts">{record.revenueCat.attemptCount}</Field>
              <Field label="Error code">
                {record.revenueCat.errorCode ? (
                  <code className="text-xs bg-red-50 text-red-700 px-1.5 py-0.5 rounded break-all">
                    {record.revenueCat.errorCode}
                  </code>
                ) : (
                  <span className="text-gray-400">None</span>
                )}
              </Field>
            </dl>
          )}
        </div>

        <div>
          <SectionHeading>Step 2 — PostHog</SectionHeading>
          {record.posthog === null ? (
            record.status === 'completed' ? (
              <p className="text-sm text-gray-500">
                Not recorded. This job completed before the PostHog step existed.
              </p>
            ) : (
              <NotReached message="Step 2 never reported. PostHog runs before the Firestore sweep, so the local data should still be intact and the job is safe to retry." />
            )
          ) : (
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Outcome">
                <PostHogOutcomeBadge audit={record.posthog} completed={record.status === 'completed'} />
              </Field>
              <Field label="Vendor attempts">{record.posthog.attemptCount}</Field>
              <Field label="Error code">
                {record.posthog.errorCode ? (
                  <code className="text-xs bg-red-50 text-red-700 px-1.5 py-0.5 rounded break-all">
                    {record.posthog.errorCode}
                  </code>
                ) : (
                  <span className="text-gray-400">None</span>
                )}
              </Field>
            </dl>
          )}
        </div>

        <div>
          <SectionHeading>
            Step 3 — Firestore sweep
            {locations.length > 0 && (
              <span className="ml-2 font-normal text-gray-500">
                {summary.documentsDeleted} document
                {summary.documentsDeleted === 1 ? '' : 's'} removed across{' '}
                {summary.locationsDeleted + summary.locationsFailed} location
                {summary.locationsDeleted + summary.locationsFailed === 1 ? '' : 's'}
              </span>
            )}
          </SectionHeading>
          {locations.length === 0 ? (
            <NotReached message="Step 3 never ran. No registered location was swept for this uid." />
          ) : (
            <div className="border border-gray-200 rounded-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Documents deleted
                      </th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Error code
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {locations.map(([locationId, result]) => (
                      <tr key={locationId}>
                        <td className="px-4 py-2 text-sm font-mono text-gray-700">{locationId}</td>
                        <td className="px-4 py-2 text-sm">
                          <span
                            className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                              result.status === 'deleted'
                                ? 'bg-green-100 text-green-700'
                                : 'bg-red-100 text-red-700'
                            }`}
                          >
                            {result.status}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-600">
                          {result.documentsDeleted}
                          {result.documentsDeleted === 0 && result.status === 'deleted' && (
                            <span className="ml-1 text-xs text-gray-400">nothing to delete</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm">
                          {result.errorCode ? (
                            <code className="text-xs bg-red-50 text-red-700 px-1.5 py-0.5 rounded">
                              {result.errorCode}
                            </code>
                          ) : (
                            <span className="text-gray-400">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="px-4 py-2 text-xs text-gray-400 bg-gray-50 border-t border-gray-200">
                Records written before subtree counting shipped report 0 for subtree locations
                whatever they removed. A 0 there is only trustworthy on a recent record.
              </p>
            </div>
          )}
        </div>

        <p className="text-xs text-gray-400 border-t border-gray-100 pt-3">
          Read-only. Recovery is the hourly retryAccountDeletions function's job. To verify one uid
          end to end, run <code>npm run verify-erasure -- --uid {record.uid}</code> in tools/.
        </p>
      </div>
    </Modal>
  );
};
